/**
 * When and how a clinic reminds its patients about tomorrow.
 *
 * Nothing here is nullable, and that is the difference from `tenantBranding`: a clinic that
 * has never opened this screen is not unconfigured, it is on at noon by email. The API answers
 * with those defaults rather than nulls, so the form has no empty state to render and an admin
 * sees what is actually happening to their patients.
 *
 * The send time is the clinic's own wall clock with no zone, like every other time in this
 * app -- `12:00` is noon at the clinic, whoever is looking.
 */

/** What GET /reminder-settings returns. */
export interface IReminderSettings {
    enabled: boolean;
    /** `HH:mm`, the way an `<input type="time">` reads it. */
    sendTime: string;
    /** What the clinic has chosen: `EMAIL`, and later `SMS` / `WHATSAPP`. */
    channels: string[];
    /**
     * What this deployment can actually send over today.
     *
     * The screen shows every channel and disables the ones missing from here, rather than
     * hiding them: an admin should be able to see that SMS is coming without being able to
     * switch on a setting nothing would act on. Adding a sender on the backend lights the
     * option up with no front-end release.
     */
    availableChannels: string[];
}

/** What PUT /reminder-settings takes. A full replace of the three, all required. */
export interface ReminderSettingsRequest {
    enabled: boolean;
    sendTime: string;
    channels: string[];
}

/**
 * What the form holds. The same shape as the response less the part it cannot edit, because
 * every field is already a form-friendly type -- there is no null to flatten to `''` here.
 */
export interface ReminderSettingsDraft {
    enabled: boolean;
    sendTime: string;
    channels: string[];
}

/** Every channel the screen draws, in the order it draws them. */
export const REMINDER_CHANNELS: string[] = ['EMAIL', 'SMS', 'WHATSAPP'];

/** What the form opens on before the first load lands. Matches the API's own defaults. */
export const defaultReminderSettingsDraft = (): ReminderSettingsDraft => ({
    enabled: true,
    sendTime: '12:00',
    channels: ['EMAIL'],
});

export const toReminderSettingsDraft = (
    settings: IReminderSettings | undefined
): ReminderSettingsDraft =>
    settings
        ? {
              enabled: settings.enabled,
              // Trimmed to HH:mm because <input type="time"> renders nothing for a value it
              // cannot parse, and a clinic whose row was edited by hand may carry seconds.
              sendTime: settings.sendTime.slice(0, 5),
              channels: settings.channels,
          }
        : defaultReminderSettingsDraft();

export const toReminderSettingsRequest = (
    draft: ReminderSettingsDraft
): ReminderSettingsRequest => ({
    enabled: draft.enabled,
    sendTime: draft.sendTime,
    channels: draft.channels,
});

/*
 * The rules Save is gated on -- a real time of day, and at least one channel unless
 * reminders are off -- live in `ClinicAdmin/reminderSettingsSchema.ts`, stated per field so
 * each lands on the control at fault. There is deliberately no predicate here saying the
 * same thing a second time.
 */
